import React from 'react';
import { Input, Space, Button, Dropdown, Spin, message, Modal, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined, EllipsisOutlined } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import CommonTable from 'components/CommonTable';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'constants/path';
import type { EFaxListRow, FaxContact } from 'types/eFax';
import { FAX_TAG_OPTIONS } from 'types/eFax';
import { faxInboxList, faxDeleteMessage, faxDownload, faxAssignPatient } from 'api/fax';
import { getProspectList } from 'api/prospect';
import type { Prospect } from 'types/common';
import {
    extractFaxPagedList,
    findFaxContactByNumber,
    mapInboundFaxVo,
    showReceivedUnreadDot,
    isFaxScheduleSuccess,
    formatFaxNumber,
} from 'utils/eFaxScheduleRows';
import { formatUTCTime } from 'utils/common';
import FaxDetailDrawer from '../FaxDetailDrawer';
import s from '../s.module.less';
import dayjs from 'utils/dayjs';
import AiTechnologySparkIcon from 'assets/figma/efax/ai-technology-spark.svg';

interface Props {
    contacts: FaxContact[];
    onUnreadCountChange?: () => void;
    onAddContact?: (faxNumber: string) => void;
    refreshKey?: number;
}

const ReceivedFaxTab: React.FC<Props> = ({ contacts, onUnreadCountChange, onAddContact, refreshKey }) => {
    const navigate = useNavigate();
    const [data, setData] = React.useState<EFaxListRow[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [searchFrom, setSearchFrom] = React.useState('');
    const [searchSummary, setSearchSummary] = React.useState('');
    const [searchPatient, setSearchPatient] = React.useState('');
    const [detailUuid, setDetailUuid] = React.useState<string | null>(null);
    const [detailOpen, setDetailOpen] = React.useState(false);
    const [assignTargetId, setAssignTargetId] = React.useState<string | null>(null);
    const [showAssignModal, setShowAssignModal] = React.useState(false);
    const [selectedPatientId, setSelectedPatientId] = React.useState<number | undefined>(undefined);
    const [assignLoading, setAssignLoading] = React.useState(false);

    const { data: prospectData, run: fetchProspects } = useRequest(getProspectList, { manual: true });
    const prospectList: Prospect[] = (prospectData?.data?.data as Prospect[] | undefined) || [];
    const clientOptions = prospectList.filter((p) => !p.isSample);

    // Antd table filter dropdown can intermittently trigger a noisy ResizeObserver loop error in Chrome,
    // which some dev overlays surface as a blocking runtime error. Suppress it only while this tab is mounted.
    React.useEffect(() => {
        const shouldSuppress = (msg: string) =>
            msg.includes('ResizeObserver loop limit exceeded') ||
            msg.includes('ResizeObserver loop completed with undelivered notifications');

        const argsContainSuppressed = (args: unknown[]): boolean => {
            for (const a of args) {
                if (typeof a === 'string' && shouldSuppress(a)) return true;
                if (a && typeof a === 'object') {
                    const maybeMessage = (a as { message?: unknown }).message;
                    if (typeof maybeMessage === 'string' && shouldSuppress(maybeMessage)) return true;
                    try {
                        const str = String(a);
                        if (typeof str === 'string' && shouldSuppress(str)) return true;
                    } catch {
                        // ignore stringification failures
                    }
                }
            }
            return false;
        };

        const originalError = console.error.bind(console);
        const originalWarn = console.warn.bind(console);
        console.error = (...args: unknown[]) => {
            if (argsContainSuppressed(args)) return;
            originalError(...args);
        };
        console.warn = (...args: unknown[]) => {
            if (argsContainSuppressed(args)) return;
            originalWarn(...args);
        };

        const onWindowError = (event: Event) => {
            const maybeMessage = (event as unknown as { message?: unknown })?.message;
            const msg = typeof maybeMessage === 'string' ? maybeMessage : '';
            if (shouldSuppress(msg)) {
                (event as unknown as { preventDefault?: () => void })?.preventDefault?.();
                (event as unknown as { stopImmediatePropagation?: () => void })?.stopImmediatePropagation?.();
            }
        };
        const onUnhandledRejection = (event: PromiseRejectionEvent) => {
            const reason = (event as unknown as { reason?: unknown })?.reason;
            let msg = '';
            if (typeof reason === 'string') {
                msg = reason;
            } else if (reason && typeof reason === 'object') {
                const maybeMessage = (reason as { message?: unknown }).message;
                if (typeof maybeMessage === 'string') msg = maybeMessage;
            }
            if (msg && shouldSuppress(msg)) {
                event.preventDefault?.();
                event.stopImmediatePropagation?.();
            }
        };

        window.addEventListener('error', onWindowError);
        window.addEventListener('unhandledrejection', onUnhandledRejection);

        return () => {
            console.error = originalError;
            console.warn = originalWarn;
            window.removeEventListener('error', onWindowError);
            window.removeEventListener('unhandledrejection', onUnhandledRejection);
        };
    }, []);

    const fetchHistory = React.useCallback(async () => {
        setLoading(true);
        const result = await faxInboxList({ current: 1, size: 50, sortBy: 'RECEIVED_ON' });
        const body = result?.data;
        if (!result.error && body) {
            const list = extractFaxPagedList(body).map(mapInboundFaxVo);
            setData(list);
        } else {
            setData([]);
        }
        setLoading(false);
    }, []);

    React.useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    React.useEffect(() => {
        if (refreshKey !== undefined && refreshKey > 0) {
            fetchHistory();
        }
    }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const filteredData = React.useMemo(() => {
        let result = data;
        if (searchFrom) {
            const lower = searchFrom.toLowerCase();
            result = result.filter((r) => {
                const matched = findFaxContactByNumber(contacts, r.faxCallerId);
                const contactLine = (matched?.name || r.contactName || r.faxCsid || '').toLowerCase();
                return r.faxCallerId.toLowerCase().includes(lower) || contactLine.includes(lower);
            });
        }
        if (searchSummary) {
            const lower = searchSummary.toLowerCase();
            result = result.filter((r) => (r.aiSummary || '').toLowerCase().includes(lower));
        }
        if (searchPatient) {
            const lower = searchPatient.toLowerCase();
            result = result.filter((r) => (r.patient || '').toLowerCase().includes(lower));
        }
        return result;
    }, [data, searchFrom, searchSummary, searchPatient, contacts]);

    const makeSearchFilterDropdown = (value: string, setValue: (v: string) => void) => {
        return () => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder="Search..."
                    style={{ marginBottom: 8, display: 'block' }}
                    onKeyDown={(e) => e.key === 'Enter' && e.stopPropagation()}
                />
                <Space>
                    <Button type="primary" icon={<SearchOutlined />} size="small" style={{ width: 90 }}>
                        Search
                    </Button>
                    <Button onClick={() => setValue('')} size="small" style={{ width: 90 }}>
                        Reset
                    </Button>
                </Space>
            </div>
        );
    };

    const handleDownload = async (record: EFaxListRow) => {
        const result = await faxDownload(record.id);
        const body = result?.data as { code?: number; data?: string } | undefined;
        if (!result?.error && body && isFaxScheduleSuccess(body) && body.data) {
            window.open(body.data, '_blank');
        } else {
            message.error('Download failed');
        }
    };

    const handleDelete = (record: EFaxListRow) => {
        Modal.confirm({
            title: 'Delete fax',
            content: 'Are you sure you want to delete this fax?',
            okText: 'Delete',
            okButtonProps: { danger: true },
            onOk: async () => {
                const result = await faxDeleteMessage(record.id);
                const body = result?.data as { code?: number; message?: string } | undefined;
                const ok = !result?.error && (!body || isFaxScheduleSuccess(body));
                if (!ok) {
                    message.error(body?.message || 'Failed to delete');
                    return;
                }
                message.success('Fax deleted');
                await fetchHistory();
                onUnreadCountChange?.();
            },
        });
    };

    const handleView = (record: EFaxListRow) => {
        setDetailUuid(record.id);
        setDetailOpen(true);
    };

    const handleOpenAssign = (record: EFaxListRow) => {
        fetchProspects();
        setAssignTargetId(record.id);
        setSelectedPatientId(undefined);
        setShowAssignModal(true);
    };

    const handleConfirmAssign = async () => {
        if (!assignTargetId || !selectedPatientId) return;
        setAssignLoading(true);
        try {
            const result = await faxAssignPatient(assignTargetId, selectedPatientId);
            const body = result?.data as { code?: number; message?: string } | undefined;
            if (result?.error || !body || !isFaxScheduleSuccess(body)) {
                message.error(body?.message || 'Failed to assign patient');
                return;
            }
            message.success('Patient assigned');
            setShowAssignModal(false);
            fetchHistory();
        } finally {
            setAssignLoading(false);
        }
    };

    const columns: ColumnsType<EFaxListRow> = [
        {
            title: 'SENT FROM',
            dataIndex: 'faxCallerId',
            width: 200,
            filterIcon: <SearchOutlined />,
            filterDropdown: makeSearchFilterDropdown(searchFrom, setSearchFrom),
            render: (_, record) => {
                const faxNum = record.faxCallerId;
                const matched = findFaxContactByNumber(contacts, record.faxCallerId);
                const inContacts = Boolean(matched);
                /**
                 * Received (Figma): contact name = address book match or server-linked contact only.
                 * Sender CSID is a separate fallback line when there is no resolved contact name.
                 * (Sent tab uses recipient / user display differently.)
                 */
                const contactDisplayName =
                    (matched?.name || record.contactName || '').trim() || undefined;
                const isUnread = showReceivedUnreadDot(record);
                const faxNumDisplay = formatFaxNumber(faxNum) || faxNum;
                return (
                    <div className={s.sentFromCell}>
                        <span className={isUnread ? s.unreadDot : s.readDot} />
                        <div className={s.sentFromInfo}>
                            {contactDisplayName ? (
                                <>
                                    <span className={s.sentFromContact}>{contactDisplayName}</span>
                                    <span className={s.sentFromSecondaryRow}>
                                        <span className={s.sentFromSecondaryText}>{faxNumDisplay}</span>
                                        {!inContacts && (
                                            <div
                                                onClick={() => onAddContact?.(record.faxCallerId)}
                                                className={s.addContactCircleBtn}
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="24"
                                                    height="24"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                >
                                                    <path
                                                        d="M12 9V12M12 12V15M12 12H15M12 12H9M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                                                        stroke="#111827"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            </div>
                                        )}
                                    </span>
                                </>
                            ) : (
                                <span className={s.sentFromPrimaryRow}>
                                    <span className={s.sentFromNumber}>{faxNumDisplay}</span>
                                    {!inContacts && (
                                         <div
                                                onClick={() => onAddContact?.(record.faxCallerId)}
                                                className={s.addContactCircleBtn}
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="24"
                                                    height="24"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                >
                                                    <path
                                                        d="M12 9V12M12 12V15M12 12H15M12 12H9M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                                                        stroke="#111827"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            </div>

                                    )}
                                </span>
                            )}
                        </div>
                    </div>
                );
            },
        },
        {
            title: 'TAG',
            dataIndex: 'tag',
            width: 160,
            filters: FAX_TAG_OPTIONS.map((t) => ({ text: t, value: t })),
            onFilter: (value, record) =>
                record.tag === value || (record.tag || '').split(',').map((t) => t.trim()).includes(String(value)),
            render: (tag: string) => (tag ? <span className={s.badgeTag}>{tag}</span> : '-'),
        },
        {
            title: 'AI SUMMARY',
            dataIndex: 'aiSummary',
            filterIcon: <SearchOutlined />,
            filterDropdown: makeSearchFilterDropdown(searchSummary, setSearchSummary),
            render: (text: string) => text || '-',
        },
        {
            title: 'PATIENT',
            dataIndex: 'patient',
            width: 150,
            filterIcon: <SearchOutlined />,
            filterDropdown: makeSearchFilterDropdown(searchPatient, setSearchPatient),
            render: (text: string, record: EFaxListRow) => {
                const patientName = (text || '').trim();
                const {patientId} = record;
                const display = patientName || (patientId ? `ID: ${patientId}` : '');
                if (!display) return <span>-</span>;

                const canNavigate = typeof patientId === 'number' && !Number.isNaN(patientId);
                return (
                    <div className={s.patientCell}>
                        {record.aiMatched && (
                            <span className={s.aiMatchedLabel}>
                                <img
                                    className={s.aiMatchedIcon}
                                    src={AiTechnologySparkIcon}
                                    alt=""
                                    aria-hidden="true"
                                />
                                <span>AI-matched</span>
                            </span>
                        )}
                        <button
                            type="button"
                            className={record.aiMatched ? s.patientLinkBtnAi : s.patientLinkBtnManual}
                            disabled={!canNavigate}
                            onClick={() => {
                                if (!canNavigate) return;
                                navigate(`${PATH.DASHBOARD}/${PATH.PROSPECTS_DETAIL}/${patientId}`);
                            }}
                        >
                            {display}
                        </button>
                    </div>
                );
            },
        },
        {
            title: 'SENT ON',
            dataIndex: 'createdAt',
            width: 160,
            sorter: (a, b) => dayjs.utc(a.createdAt).valueOf() - dayjs.utc(b.createdAt).valueOf(),
            render: (date: string) => (date ? formatUTCTime(date, 'MM/DD/YY') : '-'),
        },
        {
            title: '',
            key: 'viewActions',
            width: 100,
            render: (_, record) => {
                const inContacts = Boolean(findFaxContactByNumber(contacts, record.faxCallerId));
                return (
                    <div className={s.viewActionsCell}>
                        <button type="button" className={s.viewLinkBtn} onClick={() => handleView(record)}>
                            View
                        </button>
                        <Dropdown
                            menu={{
                                items: [
                                    ...(!inContacts ? [{ key: 'addContact', label: 'Add to contact' }] : []),
                                    ...(record.patient
                                        ? [{ key: 'reassignPatient', label: 'Reassign patient' }]
                                        : [{ key: 'assignPatient', label: 'Assign patient' }]
                                    ),
                                    { key: 'download', label: 'Download' },
                                    { key: 'delete', label: 'Delete', danger: true },
                                ],
                                onClick: ({ key }) => {
                                    if (key === 'addContact') onAddContact?.(record.faxCallerId);
                                    if (key === 'reassignPatient') handleOpenAssign(record);
                                    if (key === 'assignPatient') handleOpenAssign(record);
                                    if (key === 'download') handleDownload(record);
                                    if (key === 'delete') handleDelete(record);
                                },
                            }}
                            trigger={['click']}
                        >
                            <button type="button" className={s.iconBtn} aria-label="More actions">
                                <EllipsisOutlined className={s.ellipsisIcon} />
                            </button>
                        </Dropdown>
                    </div>
                );
            },
        },
    ];

    return (
        <>
            <Spin spinning={loading}>
                <CommonTable<EFaxListRow>
                    data={filteredData}
                    columns={columns}
                    rowKey="id"
                    pagination={{ pageSize: 20 }}
                    locale={{
                        emptyText: <div className={s.emptyText}>No faxes received yet</div>,
                    }}
                />
            </Spin>

            <FaxDetailDrawer
                open={detailOpen}
                uuid={detailUuid}
                onClose={() => setDetailOpen(false)}
                onUpdated={() => {
                    fetchHistory();
                    onUnreadCountChange?.();
                }}
            />

            <Modal
                title="Assign patient"
                open={showAssignModal}
                onCancel={() => setShowAssignModal(false)}
                onOk={handleConfirmAssign}
                okText="Assign"
                confirmLoading={assignLoading}
                okButtonProps={{ disabled: !selectedPatientId }}
                destroyOnClose
            >
                <Select
                    showSearch
                    style={{ width: '100%' }}
                    placeholder="Search patient..."
                    value={selectedPatientId}
                    onChange={(val) => setSelectedPatientId(val)}
                    filterOption={(input, option) =>
                        (option?.label as string || '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={clientOptions.map((p) => ({
                        value: p.id,
                        label: `${p.firstName} ${p.lastName}`,
                    }))}
                />
            </Modal>
        </>
    );
};

export default ReceivedFaxTab;
